import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { ChevronDown, ChevronUp, ExternalLink, Info } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type {
  CalculatorReference,
  ConditionedInput,
  EvaluatedInput,
  EvaluatedTerm,
} from '../../contracts';

const ORDINAL_COLLAPSE_THRESHOLD = 3;

interface CollapsibleCalculationProps {
  /** Backward-compatible formula string */
  formula?: string;
  /** Human-readable formula text */
  formulaHuman?: string;
  /** Machine-readable formula expression (reserved for future UI use) */
  formulaMachine?: string;
  /** Evaluated inputs with their values for display (formula-based calculators) */
  evaluatedInputs?: EvaluatedInput[];
  /** Evaluated terms with contribution points (scoring calculators) */
  evaluatedTerms?: EvaluatedTerm[];
  /** Decision-tree map for conditional calculators (e.g., C-SSRS, PECARN) */
  conditionalityMap?: ConditionedInput[];
  /** Unit for the result (e.g., "kg/m²", "mL/min/1.73m²") */
  unit?: string;
  /** Reference citation for the calculator source paper */
  reference?: CalculatorReference;
  /** Additional CSS classes */
  className?: string;
}

/**
 * CollapsibleCalculation – Figma "Calculator / Simple" expanded section (EVI-730).
 *
 * Features:
 * - Hidden by default with "See calculation" toggle
 * - Calculation list: criteria rows (scoring) or input rows (formula)
 * - For scoring calculators: shows Yes/No with actual contribution points; bolds active values
 * - Ordinal inputs: adaptive collapse for dense rows (4+ ranges)
 * - Formula section with (i) tooltip showing reference details
 * - i18n support with fallback to English labels
 */
export const CollapsibleCalculation = ({
  formula,
  formulaHuman,
  evaluatedInputs,
  evaluatedTerms,
  conditionalityMap,
  reference,
  className,
}: CollapsibleCalculationProps): JSX.Element | null => {
  // Backend-provided `label_key`s (one subtree per calculator) still resolve
  // through the legacy i18next catalog; everything static here is react-intl.
  const { t } = useTranslations();
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedOrdinals, setExpandedOrdinals] = useState<Set<string>>(new Set());

  const toggleOrdinalExpand = useCallback((variable: string) => {
    setExpandedOrdinals((prev) => {
      const next = new Set(prev);
      if (next.has(variable)) {
        next.delete(variable);
      } else {
        next.add(variable);
      }
      return next;
    });
  }, []);

  const hasTerms = evaluatedTerms && evaluatedTerms.length > 0;
  const hasInputs = evaluatedInputs && evaluatedInputs.length > 0;
  const termVariables = new Set((evaluatedTerms ?? []).map((term) => term.variable));
  const nonOverlappingInputs = (evaluatedInputs ?? []).filter(
    (input) => !termVariables.has(input.variable)
  );
  const hasNonOverlappingInputs = nonOverlappingInputs.length > 0;
  const hasConditionality = conditionalityMap && conditionalityMap.length > 0;
  const displayFormula = formulaHuman || formula;

  if (!displayFormula && !hasTerms && !hasInputs && !hasConditionality) {
    return null;
  }

  const getLabel = (labelKey: string, fallback: string): string => {
    const translated = t(labelKey as TranslationKey, { defaultValue: '' });
    if (!translated || translated === labelKey) return fallback;
    return translated;
  };

  const formatNumericValue = (value: number): string => {
    return Number.isInteger(value)
      ? value.toString()
      : parseFloat(value.toFixed(2)).toString();
  };

  const formatPoints = (points: number | string): string =>
    typeof points === 'number' ? (points > 0 ? `+${points}` : String(points)) : points;

  const yesLabel = intl.formatMessage({
    defaultMessage: 'Yes',
    id: 'oMTYuLsxJt',
    description: 'Positive value on a calculator criterion toggle',
  });
  const noLabel = intl.formatMessage({
    defaultMessage: 'No',
    id: '8jej0VUbnX',
    description: 'Negative value on a calculator criterion toggle',
  });

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Toggle button */}
      <Button
        type="button"
        variant="ghost"
        size="xs"
        className="w-fit gap-1 text-text-tertiary"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <FormattedMessage
            defaultMessage="Hide calculation"
            id="xFuGVLBuiI"
            description="Collapses the calculation details on a calculator block"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Show calculation"
            id="UeN/sRx/2/"
            description="Expands the calculation details on a calculator block"
          />
        )}
        {isOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
      </Button>

      {/* Expanded calculation display */}
      {isOpen && (
        <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-top-1">
          {/* Calculation list */}
          {(hasTerms || hasInputs || hasConditionality) && (
            <div className="flex flex-col gap-2">
              <TypographyCaption className="px-2 text-text-tertiary">
                <FormattedMessage
                  defaultMessage="Calculation"
                  id="ZBsfRMY3Um"
                  description="Heading above the criteria/input rows on a calculator block"
                />
              </TypographyCaption>

              <div className="flex flex-col">
                {/* Decision-tree rows (conditionalityMap) — for conditional calculators */}
                {hasConditionality &&
                  !hasTerms &&
                  !hasInputs &&
                  conditionalityMap.map((item) => {
                    const isSkipped = item.status === 'skipped';
                    const label = getLabel(item.label_key, item.label);
                    const isPositive =
                      item.value === true ||
                      (typeof item.value === 'number' && item.value > 0);

                    return (
                      <div
                        key={item.variable}
                        className={cn(
                          'flex items-center justify-between gap-3 py-1.5',
                          item.depth === 0 ? 'px-2' : 'pl-6 pr-2',
                          isSkipped && 'opacity-40'
                        )}
                      >
                        <span
                          className={cn(
                            'text-sm',
                            isSkipped
                              ? 'text-text-tertiary'
                              : isPositive
                                ? 'font-medium text-text-primary'
                                : 'text-text-primary'
                          )}
                        >
                          {label}
                        </span>

                        {isSkipped ? (
                          <span className="shrink-0 text-xs text-text-tertiary">
                            <FormattedMessage
                              defaultMessage="N/A"
                              id="CSZzEx22Vl"
                              description="Shown for a skipped criterion in a decision-tree calculator"
                            />
                          </span>
                        ) : (
                          <span className="flex shrink-0 overflow-hidden rounded-md border border-border text-xs">
                            <span
                              className={cn(
                                'flex w-12 items-center justify-center py-1',
                                !isPositive
                                  ? 'bg-text-secondary font-medium text-white'
                                  : 'bg-transparent text-text-tertiary'
                              )}
                            >
                              {noLabel}
                            </span>
                            <span
                              className={cn(
                                'flex w-12 items-center justify-center py-1',
                                isPositive
                                  ? 'bg-validation-destructive font-medium text-white'
                                  : 'bg-transparent text-text-tertiary'
                              )}
                            >
                              {yesLabel}
                            </span>
                          </span>
                        )}
                      </div>
                    );
                  })}

                {/* Scoring calculator rows (evaluatedTerms) — dual-value toggle */}
                {hasTerms &&
                  evaluatedTerms.map((term) => {
                    const isActive =
                      term.value === true ||
                      (typeof term.value === 'number' && term.value > 0);
                    const label = getLabel(term.label_key, term.label);

                    const activePoints =
                      typeof term.coefficient === 'number'
                        ? term.coefficient
                        : (term.contribution ?? 0);
                    const formattedPoints =
                      activePoints > 0 ? `+${activePoints}` : String(activePoints);

                    return (
                      <div
                        key={term.variable}
                        className="flex items-center justify-between gap-3 px-2 py-1.5"
                      >
                        <span
                          className={cn(
                            'text-sm text-text-primary',
                            isActive && 'font-medium'
                          )}
                        >
                          {label}
                        </span>

                        {/* Connected toggle: No 0 | Yes +N */}
                        <span className="flex shrink-0 overflow-hidden rounded-md border border-border text-xs tabular-nums">
                          <span
                            className={cn(
                              'flex w-16 items-center justify-center gap-1 py-1',
                              !isActive
                                ? 'bg-text-secondary font-medium text-white'
                                : 'bg-transparent text-text-tertiary'
                            )}
                          >
                            {noLabel} 0
                          </span>
                          <span
                            className={cn(
                              'flex w-16 items-center justify-center gap-1 py-1',
                              isActive
                                ? 'bg-validation-success font-medium text-white'
                                : 'bg-transparent text-text-tertiary'
                            )}
                          >
                            {yesLabel} {formattedPoints}
                          </span>
                        </span>
                      </div>
                    );
                  })}

                {/* Formula/input rows — only render inputs not already represented by terms */}
                {hasNonOverlappingInputs &&
                  nonOverlappingInputs.map((input) => {
                    const label = getLabel(input.label_key, input.label);
                    const hasOrdinalRanges =
                      input.ordinal_ranges && input.ordinal_ranges.length > 0;
                    const activeScore =
                      typeof input.contribution === 'number'
                        ? input.contribution
                        : typeof input.value === 'number'
                          ? input.value
                          : undefined;

                    if (hasOrdinalRanges && activeScore !== undefined) {
                      const ranges = input.ordinal_ranges!;
                      const backendSelectedIndex = input.selected_ordinal_range_index;
                      const hasValidBackendSelection =
                        typeof backendSelectedIndex === 'number' &&
                        backendSelectedIndex >= 0 &&
                        backendSelectedIndex < ranges.length;

                      const matchingRangeIndices = ranges
                        .map((range, i) => (range.score === activeScore ? i : -1))
                        .filter((i) => i >= 0);

                      const inferredUniqueIndex =
                        matchingRangeIndices.length === 1 ? matchingRangeIndices[0]! : -1;
                      const activeIndex = hasValidBackendSelection
                        ? backendSelectedIndex
                        : inferredUniqueIndex;

                      const shouldCollapse = ranges.length >= ORDINAL_COLLAPSE_THRESHOLD;
                      const isExpanded = expandedOrdinals.has(input.variable);
                      const activeRange =
                        activeIndex >= 0 ? ranges[activeIndex] : undefined;

                      // Few ranges (< threshold): original inline segmented layout
                      if (!shouldCollapse) {
                        return (
                          <div
                            key={input.variable}
                            className="flex items-center justify-between gap-3 px-2 py-1.5"
                          >
                            <span
                              className={cn(
                                'text-sm text-text-primary',
                                activeScore > 0 && 'font-medium'
                              )}
                            >
                              {label}
                            </span>
                            <span className="flex shrink-0 items-center gap-1.5">
                              <span className="flex shrink-0 overflow-hidden rounded-md border border-border text-xs tabular-nums">
                                {ranges.map((range, i) => {
                                  const isRangeActive = i === activeIndex;
                                  return (
                                    <span
                                      key={`${input.variable}-${i}`}
                                      className={cn(
                                        'flex items-center justify-center px-2 py-1',
                                        isRangeActive
                                          ? activeScore > 0
                                            ? 'bg-validation-success font-medium text-white'
                                            : 'bg-text-secondary font-medium text-white'
                                          : 'bg-transparent text-text-tertiary'
                                      )}
                                    >
                                      {range.label} {formatPoints(range.score)}
                                    </span>
                                  );
                                })}
                              </span>
                              {/* Spacer matching chevron width for alignment with collapsible rows */}
                              <span className="size-3 shrink-0" aria-hidden="true" />
                            </span>
                            {activeIndex < 0 && (
                              <span className="shrink-0 rounded-md border border-border px-2 py-0.5 text-xs text-text-tertiary">
                                <FormattedMessage
                                  defaultMessage="Selected {points}"
                                  id="FbYL3Mw8gZ"
                                  values={{ points: formatPoints(activeScore) }}
                                  description="Points chip when the selected ordinal range is unknown"
                                />
                              </span>
                            )}
                          </div>
                        );
                      }

                      // Many ranges (>= threshold), collapsed: show only selected chip
                      if (!isExpanded) {
                        return (
                          <div key={input.variable}>
                            <button
                              type="button"
                              className="ease flex w-full items-center justify-between gap-3 px-2 py-1.5 transition-colors duration-200 hover:bg-surface-hover"
                              onClick={() => toggleOrdinalExpand(input.variable)}
                              aria-expanded={false}
                              aria-label={intl.formatMessage(
                                {
                                  defaultMessage: 'Expand {label} scoring ranges',
                                  id: '4c/2hW/qEw',
                                  description:
                                    'Accessible label for expanding an ordinal input’s score ranges',
                                },
                                { label }
                              )}
                            >
                              <span
                                className={cn(
                                  'text-sm text-text-primary',
                                  activeScore > 0 && 'font-medium'
                                )}
                              >
                                {label}
                              </span>
                              <span className="flex shrink-0 items-center gap-1.5">
                                {activeRange ? (
                                  <span
                                    className={cn(
                                      'flex items-center justify-center rounded-md border border-border px-2 py-1 text-xs tabular-nums',
                                      activeScore > 0
                                        ? 'bg-validation-success font-medium text-white'
                                        : 'bg-text-secondary font-medium text-white'
                                    )}
                                  >
                                    {activeRange.label} {formatPoints(activeRange.score)}
                                  </span>
                                ) : (
                                  <span className="shrink-0 rounded-md border border-border px-2 py-1 text-xs tabular-nums text-text-tertiary">
                                    <FormattedMessage
                                      defaultMessage="Selected {points}"
                                      id="FbYL3Mw8gZ"
                                      values={{ points: formatPoints(activeScore) }}
                                      description="Points chip when the selected ordinal range is unknown"
                                    />
                                  </span>
                                )}
                                <ChevronDown className="size-3 text-text-tertiary" />
                              </span>
                            </button>
                          </div>
                        );
                      }

                      // Many ranges (>= threshold), expanded: label + chevron, then wrapped chips
                      return (
                        <div key={input.variable}>
                          <button
                            type="button"
                            className="ease flex w-full items-center justify-between gap-3 px-2 py-1.5 transition-colors duration-200 hover:bg-surface-hover"
                            onClick={() => toggleOrdinalExpand(input.variable)}
                            aria-expanded={true}
                            aria-label={intl.formatMessage(
                              {
                                defaultMessage: 'Collapse {label} scoring ranges',
                                id: 'g/s87EBEMz',
                                description:
                                  'Accessible label for collapsing an ordinal input’s score ranges',
                              },
                              { label }
                            )}
                          >
                            <span
                              className={cn(
                                'text-sm text-text-primary',
                                activeScore > 0 && 'font-medium'
                              )}
                            >
                              {label}
                            </span>
                            <ChevronUp className="size-3 shrink-0 text-text-tertiary" />
                          </button>
                          <div className="flex shrink-0 flex-wrap justify-end gap-1 px-2 pb-1.5 animate-in fade-in slide-in-from-top-1">
                            {ranges.map((range, i) => {
                              const isRangeActive = i === activeIndex;
                              return (
                                <span
                                  key={`${input.variable}-${i}`}
                                  className={cn(
                                    'flex items-center justify-center rounded-md border border-border px-2 py-1 text-xs tabular-nums',
                                    isRangeActive
                                      ? activeScore > 0
                                        ? 'bg-validation-success font-medium text-white'
                                        : 'bg-text-secondary font-medium text-white'
                                      : 'bg-transparent text-text-tertiary'
                                  )}
                                >
                                  {range.label} {formatPoints(range.score)}
                                </span>
                              );
                            })}
                          </div>
                          {activeIndex < 0 && (
                            <span className="mx-2 mb-1.5 shrink-0 self-end rounded-md border border-border px-2 py-0.5 text-xs text-text-tertiary">
                              <FormattedMessage
                                defaultMessage="Selected {points}"
                                id="FbYL3Mw8gZ"
                                values={{ points: formatPoints(activeScore) }}
                                description="Points chip when the selected ordinal range is unknown"
                              />
                            </span>
                          )}
                        </div>
                      );
                    }

                    const displayValue =
                      typeof input.value === 'boolean'
                        ? input.value
                          ? yesLabel
                          : noLabel
                        : typeof input.value === 'number'
                          ? formatNumericValue(input.value)
                          : String(input.value);

                    const contribution =
                      typeof input.contribution === 'number'
                        ? input.contribution
                        : undefined;
                    const hasContribution = contribution !== undefined;

                    return (
                      <div
                        key={input.variable}
                        className="flex items-center justify-between gap-2 px-2 py-1.5"
                      >
                        <span
                          className={cn(
                            'text-sm text-text-primary',
                            hasContribution && contribution > 0 && 'font-medium'
                          )}
                        >
                          {label}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 text-sm text-text-secondary">
                          <span>
                            {displayValue}
                            {input.unit && input.unit.toLowerCase() !== 'boolean' && (
                              <span className="ml-1 text-text-tertiary">
                                {input.unit}
                              </span>
                            )}
                          </span>
                          {hasContribution && (
                            <span
                              className={cn(
                                'rounded-md border border-border px-2 py-0.5 text-xs tabular-nums',
                                contribution > 0
                                  ? 'bg-validation-success font-medium text-white'
                                  : 'bg-transparent text-text-tertiary'
                              )}
                            >
                              {contribution > 0 ? `+${contribution}` : '0'}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Formula section */}
          {displayFormula && (
            <div className="flex flex-col gap-2 px-2">
              <div className="flex items-center gap-1">
                <TypographyCaption className="text-text-tertiary">
                  <FormattedMessage
                    defaultMessage="Formula"
                    id="u1McSHihDE"
                    description="Heading above the formula text on a calculator block"
                  />
                </TypographyCaption>

                {/* (i) info icon with reference tooltip */}
                {reference && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center text-text-tertiary transition-colors hover:text-text-secondary"
                          aria-label={intl.formatMessage({
                            defaultMessage: 'Formula reference',
                            id: 'kTgeGOEyBa',
                            description:
                              'Accessible label for the info icon showing the formula citation',
                          })}
                        >
                          <Info className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent sideOffset={4} className="max-w-xs">
                        <div className="flex flex-col gap-1">
                          <span className="text-xs">{reference.citation}</span>
                          {reference.url && (
                            <a
                              href={reference.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-text-link underline"
                            >
                              <FormattedMessage
                                defaultMessage="View source"
                                id="u659jGsgH1"
                                description="Link to the calculator’s source paper in the reference tooltip"
                              />
                              <ExternalLink className="size-3" />
                            </a>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>

              {/* Human-readable formula */}
              <div className="rounded-xl bg-surface-page p-4">
                <p className="text-xs leading-relaxed text-text-primary">
                  {displayFormula}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
